import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import MultiCheckBox from '../../formComponents/MultiCheckBox';
import CheckBox from '../../formComponents/CheckBox';
import TextBox from '../../formComponents/TextBox';
import SelectMenu from '../../formComponents/SelectMenu';
import NumberInput from '../../formComponents/NumberInput';
import TextInput from '../../formComponents/TextInput';
import Notification from '../../formComponents/Notification';

export default function TreatmentsForm() {
  const { authTokens, logoutUser, baseUrl, user } = useContext(AuthContext);
  const [treatmentLabelColor, setTreatmentLabelColor] =
    useState('text-brandText');
  const [treatmentBorderColor, setTreatmentBorderColor] =
    useState('border-gray-300');
  const [descriptionLabelColor, setDescriptionLabelColor] =
    useState('text-brandText');
  const [descriptionBorderColor, setDescriptionBorderColor] =
    useState('border-gray-300');
  const [schoolLabelColor, setSchoolLabelColor] = useState('text-brandText');
  const [schoolBorderColor, setSchoolBorderColor] = useState('border-gray-300');

  /* state variables to hold json data from the back-end */
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [schools, setSchools] = useState([]);

  /* state variables to hold form data to submit to the back-end */
  const [treatmentName, setTreatmentName] = useState('');
  const [category, setCategory] = useState([]);
  const [subject, setSubject] = useState([]);
  const [treatmentType, setTreatmentType] = useState();
  const [rti, setRti] = useState('');
  const [description, setDescription] = useState('');
  const [standardDuration, setStandardDuration] = useState(1);
  const [checkDuration, setCheckDuration] = useState(1);
  const [school, setSchool] = useState([]);
  const [watchlist, setWatchlist] = useState(false);

  const [show, setShow] = useState(false);

  const rtiData = [{ name: '1' }, { name: '2' }, { name: '3' }];

  const validateFormData = (e) => {
    e.preventDefault();
    if (
      treatmentName?.trim().length >= 1 &&
      description?.trim().length >= 1 &&
      school?.length >= 1
    ) {
      setTreatmentLabelColor('text-brandText');
      setTreatmentBorderColor('border-gray-300');
      setDescriptionLabelColor('text-brandText');
      setDescriptionBorderColor('border-gray-300');
      setSchoolLabelColor('text-brandText');
      setSchoolBorderColor('border-gray-300');
      handleSubmit(e);
    } else {
      if (treatmentName?.trim().length >= 1) {
        setTreatmentLabelColor('text-brandText');
        setTreatmentBorderColor('border-gray-300');
      } else {
        setTreatmentLabelColor('text-red-500');
        setTreatmentBorderColor('border-red-500');
      }

      if (description?.trim().length >= 1) {
        setDescriptionLabelColor('text-brandText');
        setDescriptionBorderColor('border-gray-300');
      } else {
        setDescriptionLabelColor('text-red-500');
        setDescriptionBorderColor('border-red-500');
      }

      if (school?.length >= 1) {
        setSchoolLabelColor('text-brandText');
        setSchoolBorderColor('border-gray-300');
      } else {
        setSchoolLabelColor('text-red-500');
        setSchoolBorderColor('border-red-500');
      }

      setShow(true);
    }
  };

  useEffect(() => {
    getCategories();
    getSubjects();
    getTreatments();
    getSchools();
  }, []);

  const navigate = useNavigate();

  let handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response = await fetch(`${baseUrl}/mtss/treatments/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + String(authTokens.access),
        },
        body: JSON.stringify({
          treatment_name: treatmentName,
          category: category,
          subject: subject,
          treatment_type: treatmentType.id,
          rti: rti.id,
          description: description,
          standard_duration: standardDuration,
          check_duration: checkDuration,
          school: school,
          watchlist: watchlist,
          status: true,
        }),
      });

      let data = await response.json();

      if (response.status === 201) {
        navigate(-1);
      } else {
        setShow(true);
        console.log(data);

        let errorResponse = await fetch(`${baseUrl}/support/error-log/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            page: '/mtss/add-treatment',
            district: baseUrl,
            user: user.name,
            error_description:
              'User failed to add a new treatment and received a status code.',
            response_text: JSON.stringify(data),
          }),
        });
      }
    } catch (error) {
      console.log(error);

      let errorResponse = await fetch(`${baseUrl}/support/error-log/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: '/mtss/add-treatment',
          district: baseUrl,
          user: user.name,
          error_description:
            'User failed to add a new treatment and received an error.',
          response_text: JSON.stringify(error),
        }),
      });
    }
  };

  let getCategories = async () => {
    let response = await fetch(`${baseUrl}/mtss/categories/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + String(authTokens.access),
      },
    });
    let data = await response.json();

    if (response.status === 200) {
      setCategories(data);
    } else if (response.statusText === 'Unauthorized') {
      logoutUser();
    }
  };

  let getSubjects = async () => {
    let response = await fetch(`${baseUrl}/mtss/subjects/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + String(authTokens.access),
      },
    });
    let data = await response.json();

    if (response.status === 200) {
      setSubjects(data);
    } else if (response.statusText === 'Unauthorized') {
      logoutUser();
    }
  };

  let getTreatments = async () => {
    let response = await fetch(`${baseUrl}/mtss/treatment-types/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + String(authTokens.access),
      },
    });
    let data = await response.json();

    if (response.status === 200) {
      setTreatments(data);
    } else if (response.statusText === 'Unauthorized') {
      logoutUser();
    }
  };

  let getSchools = async () => {
    let response = await fetch(`${baseUrl}/sis/schools/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + String(authTokens.access),
      },
    });
    let data = await response.json();

    if (response.status === 200) {
      setSchools(data);
    } else if (response.statusText === 'Unauthorized') {
      logoutUser();
    }
  };

  return (
    <div>
      <form
        className='mx-auto flex max-w-3xl flex-col space-y-8 px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20'
        action=''
      >
        <TextInput
          name={'treatment_name'}
          displayName={'Treatment Name*'}
          data={treatmentName}
          setData={setTreatmentName}
          labelColor={treatmentLabelColor}
          borderColor={treatmentBorderColor}
        />
        <MultiCheckBox
          title={'Category'}
          hint={''}
          data={categories}
          setSelectedData={setCategory}
          dotName={'category_name'}
        />
        <MultiCheckBox
          title={'Subject'}
          hint={''}
          data={subjects}
          setSelectedData={setSubject}
          dotName={'subject_name'}
        />
        <SelectMenu
          label={'Is this a support or intervention?'}
          data={treatments}
          selectedData={treatmentType}
          setSelectedData={setTreatmentType}
          dotSelector={'treatment_type'}
        />
        <SelectMenu
          label={'Which RTI tier does this action fall into?'}
          data={rtiData}
          selectedData={rti}
          setSelectedData={setRti}
          dotSelector={'name'}
        />
        <TextBox
          name={'description'}
          displayName={'Description*'}
          rows={4}
          data={description}
          setData={setDescription}
          labelColor={descriptionLabelColor}
          borderColor={descriptionBorderColor}
        />
        <NumberInput
          name={'standard_duration'}
          label={'How many days does this typically last?'}
          data={standardDuration}
          setData={setStandardDuration}
        />
        <NumberInput
          name={'check_duration'}
          label={'Check-in after how many days??'}
          data={checkDuration}
          setData={setCheckDuration}
        />
        <MultiCheckBox
          title={'Associated Schools*'}
          hint={''}
          data={schools}
          setSelectedData={setSchool}
          dotName={'name'}
          labelColor={schoolLabelColor}
          borderColor={schoolBorderColor}
        />
        <CheckBox
          label={'Watchlist Status'}
          srName={'Include on the Watchlist?'}
          id={'watchlist'}
          name={'watchlist'}
          text={'Include on the Watchlist?'}
          textHint={''}
          isChecked={watchlist}
          setIsChecked={setWatchlist}
        />
        <button
          onClick={validateFormData}
          className='inline-flex w-full items-center justify-center rounded-md border border-transparent bg-brandPrimary px-6 py-3 text-base font-medium text-white shadow-sm duration-300 ease-in-out hover:scale-105 hover:bg-brandPrimaryLight focus:outline-none focus:ring-2 focus:ring-brandPrimary focus:ring-offset-2'
        >
          Submit
        </button>
      </form>
      <Notification
        success={false}
        show={show}
        setShow={setShow}
        messageOne={'Failed to submit form!'}
        messageTwo={
          'Please ensure that all fields and selections have been completed.'
        }
      />
    </div>
  );
}
